import {
	getLivestockDashboardQueryOptions,
	listHerdsByOrganizationQueryOptions,
} from "@packages/api-client/hooks/livestockController";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ClipboardList, Plus, Upload } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyDashboard } from "@/components/EmptyDashboard";
import { CowIcon } from "@/components/icons/CowIcon";
import { OfflineActionGuard } from "@/components/OfflineActionGuard";
import { type TabItem, TabNavigation } from "@/components/TabNavigation";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ResponsiveFormDialog } from "@/components/ui/responsive-form-dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { CardSkeleton, DashboardSkeleton } from "@/components/ui/skeleton";
import { useEstablishment } from "@/hooks/useEstablishment";
import { useFarmScope } from "@/hooks/useFarmScope";
import { useOfflineGuard } from "@/hooks/useOfflineGuard";
import { AllAnimalsList } from "./-components/AllAnimalsList";
import { AllHerdsList } from "./-components/AllHerdsList";
import {
	type AnimalFilterSearch,
	filtersToSearch,
	searchToFilters,
} from "./-components/animalFilterUrl";

// One page for the whole section: the herds + animals lists, switched in place.
// The old standalone Overview, Measurements, and Operations tabs are gone —
// measurements/operations now live inside each animal/herd detail page, and the
// overview's only durable signal (the herd/animal counts) rides on the switcher.
const LIVESTOCK_TABS = ["herds", "animals"] as const;
type LivestockTab = (typeof LIVESTOCK_TABS)[number];

// The URL is the source of filter truth for the animals tab (survives Back,
// shareable). `farm`/`tab` are the section's own keys; the rest are the cohort
// filter, serialized/parsed through `animalFilterUrl` so the flat URL shape and
// the shared `AnimalFilters` contract can never drift.
type LivestockSearch = {
	farm?: string;
	tab?: LivestockTab;
} & AnimalFilterSearch;

// Which destination the All-Farms farm picker is targeting (a herd, an animal and
// an import all belong to one farm, so each needs a concrete establishment
// first). `import` rides the same picker rather than a second one — the question
// it asks the producer is identical.
type AddTarget = "herd" | "animal" | "import";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/livestock/")({
	staticData: { offline: "ready" },
	validateSearch: (search: Record<string, unknown>): LivestockSearch => ({
		// Preserve the org-level farm scope param across this route's validation.
		farm: typeof search.farm === "string" ? search.farm : undefined,
		tab: LIVESTOCK_TABS.includes(search.tab as LivestockTab)
			? (search.tab as LivestockTab)
			: undefined,
		// Round-trip the cohort filter through the shared mapping: parse to the
		// contract shape (dropping anything malformed), then re-serialize to the
		// canonical flat URL keys so hand-edited params get normalized.
		...filtersToSearch(searchToFilters(search)),
	}),
	component: LivestockIndex,
});

function LivestockIndex() {
	const { orgId } = Route.useParams();
	const { tab, herds: herdsFilter } = Route.useSearch();
	const { t } = useTranslation();
	const { selectedEstablishmentId, establishments, isLoading } =
		useEstablishment();
	const scope = useFarmScope();
	const { blocked } = useOfflineGuard();
	const navigate = Route.useNavigate();
	const farmPickerId = useId();

	// Holds the chosen target farm + entity while All Farms is active.
	const [addTarget, setAddTarget] = useState<AddTarget | null>(null);
	const [pickerFarmId, setPickerFarmId] = useState("");

	// Preselect the target farm when it is inferable, so the All-Farms create
	// dialog opens ready-to-continue instead of on an empty picker. Two honest
	// signals: a single-farm org (no choice to make), or an animals-tab cohort
	// filtered to exactly one herd — a herd belongs to one farm, so that farm is
	// known ("herd chosen first ⇒ farm known"). The herd lookup runs only in that
	// narrow case; anything ambiguous keeps the manual picker.
	const singleHerdFilter =
		typeof herdsFilter === "string" && !herdsFilter.includes(",")
			? herdsFilter
			: undefined;
	const needHerdResolve =
		scope.kind === "all" &&
		(tab ?? "herds") === "animals" &&
		!!singleHerdFilter;
	const { data: herdsForInfer } = useQuery({
		...listHerdsByOrganizationQueryOptions(orgId, { limit: 200 }),
		enabled: needHerdResolve,
	});
	const inferredFarmId =
		establishments.length === 1
			? establishments[0]?.id
			: needHerdResolve
				? herdsForInfer?.data?.find((h) => h.id === singleHerdFilter)
						?.establishmentId
				: undefined;

	// Counts ride on the switcher so the page needs no separate dashboard. The
	// query is scope-aware — All Farms aggregates, a single farm filters — and
	// mirrors whatever the top-bar selector shows.
	const { data: dashboard, isLoading: isDashboardLoading } = useQuery(
		getLivestockDashboardQueryOptions(orgId, {
			establishmentId:
				scope.kind === "single" ? scope.establishmentId : undefined,
		}),
	);

	const TARGET_ROUTE = {
		herd: "/app/org/$orgId/livestock/$establishmentId/herds/new",
		animal: "/app/org/$orgId/livestock/$establishmentId/animals/new",
		import: "/app/org/$orgId/livestock/$establishmentId/animals/import",
	} as const;

	const goToNew = (target: AddTarget, establishmentId: string) =>
		navigate({
			to: TARGET_ROUTE[target],
			params: { orgId, establishmentId },
		});

	// Single farm → straight to the form; All Farms → pick the target farm first,
	// pre-filled with the inferred farm when there is one.
	const handleAdd = (target: AddTarget) => {
		if (scope.kind === "single") {
			goToNew(target, scope.establishmentId);
			return;
		}
		setPickerFarmId(inferredFarmId ?? "");
		setAddTarget(target);
	};

	const handleConfirmFarm = () => {
		if (!pickerFarmId || !addTarget) return;
		const target = addTarget;
		setAddTarget(null);
		goToNew(target, pickerFarmId);
	};

	if (isLoading) {
		return <DashboardSkeleton />;
	}

	// No farms - show empty state with guidance.
	if (establishments.length === 0) {
		return (
			<div className="flex flex-col gap-6">
				<EmptyDashboard
					title={t("livestock.farm.noFarms")}
					description={t("livestock.farm.noFarmsDescription")}
					icon={CowIcon}
					action={{
						label: t("common.establishments"),
						to: "/app/org/$orgId/settings/establishments",
						params: { orgId },
					}}
				/>
			</div>
		);
	}

	// Herds lead — they organize the animals beneath them — but an org that has
	// animals and no herds yet would land on an empty Herds list and have to find
	// its own animals. So with no explicit `?tab=` the default is derived from the
	// counts the switcher already loads. An explicit `?tab=` always wins (Back and
	// shared links are unaffected), and every tab link writes `tab` explicitly, so
	// a click never re-derives.
	const derivedTab: LivestockTab | undefined = dashboard
		? dashboard.kpis.totalHerds === 0 && dashboard.kpis.totalAnimals >= 1
			? "animals"
			: "herds"
		: undefined;
	// No `?tab=` and the counts are in flight: hold the list body on a skeleton
	// rather than commit to Herds, render its empty state, and switch a moment
	// later. Offline with the counts never cached, the first attempt fails and its
	// retry pauses: `isLoading` is then false, so the hub falls back to Herds
	// instead of holding the skeleton forever.
	const isDerivingTab = !tab && isDashboardLoading;
	const activeTab: LivestockTab = tab ?? derivedTab ?? "herds";
	const farm = selectedEstablishmentId ?? undefined;

	const tabs: TabItem[] = [
		{
			key: "herds",
			label: t("livestock.tabs.herds"),
			to: "/app/org/$orgId/livestock",
			params: { orgId },
			search: { tab: "herds", farm },
			icon: <ClipboardList className="h-4 w-4" />,
			badge: dashboard?.kpis.totalHerds,
		},
		{
			key: "animals",
			label: t("livestock.tabs.animals"),
			to: "/app/org/$orgId/livestock",
			params: { orgId },
			search: { tab: "animals", farm },
			icon: <CowIcon className="h-4 w-4" />,
			badge: dashboard?.kpis.totalAnimals,
		},
	];

	// One definition, two placements: the desktop toolbar on the right of the
	// header row, and the phone's eyebrow row, where `compact` trades the verb
	// away — "Animal" and "Importar" fit beside "Rebanho", "Adicionar animal"
	// does not.
	const addActions = (compact: boolean) => {
		const size = compact ? "sm" : "default";
		const chip = compact ? "h-7 gap-1 px-2 text-[12px]" : undefined;
		const icon = compact ? "h-3.5 w-3.5" : "mr-2 h-4 w-4";
		return activeTab === "herds" ? (
			<OfflineActionGuard blocked={blocked}>
				<Button
					size={size}
					className={chip}
					onClick={() => handleAdd("herd")}
					aria-disabled={blocked}
				>
					<Plus className={icon} />
					{compact
						? t("livestock.dashboard.quickActions.addHerdShort")
						: t("livestock.dashboard.quickActions.addHerd")}
				</Button>
			</OfflineActionGuard>
		) : (
			<>
				{/* Import sits beside — not instead of — `+ Animal`: it is the door
				    for a producer who already has the herd in a file, and the
				    online-only guard is what stops it offline. */}
				<OfflineActionGuard blocked={blocked}>
					<Button
						variant="outline"
						size={size}
						className={chip}
						onClick={() => handleAdd("import")}
						aria-disabled={blocked}
					>
						<Upload className={icon} />
						{t("livestock.animalImport.importActionShort")}
					</Button>
				</OfflineActionGuard>
				<Button
					size={size}
					className={chip}
					onClick={() => handleAdd("animal")}
				>
					<Plus className={icon} />
					{compact
						? t("livestock.animal.create.buttonShort")
						: t("livestock.animal.create.button")}
				</Button>
			</>
		);
	};

	return (
		// Tighter stack on a phone: the switcher is a header for the list below it,
		// so it sits closer to it than the desktop toolbar rhythm allows.
		<div className="flex flex-col gap-3 md:gap-6">
			{/* No title — the sidebar already identifies the section, and the top
			    farm selector identifies the scope. From md up (no bottom nav) the add
			    buttons sit on the right of the toolbar row; on a phone they ride the
			    eyebrow row instead, compactly — the bottom nav's Register button is a
			    global shortcut, not a create action for the list you are looking
			    at. */}
			<div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between">
				{/* Add actions matching the active tab (herds → + Lote, animals →
				    Importar + Animal). Desktop only — the phone renders the same set
				    compactly on the eyebrow row below. */}
				<div className="hidden items-center gap-2 md:order-2 md:flex">
					{addActions(false)}
				</div>
				{/* The eyebrow names the section the switcher belongs to, so the
				    tab labels can stay bare nouns ("Lotes" / "Animais") instead of
				    carrying the context themselves. */}
				<div className="flex min-w-0 flex-col gap-1.5 md:order-1">
					<div className="flex min-h-8 items-center justify-between gap-2">
						<span className="font-bold text-[12px] text-base-content/70 uppercase tracking-[0.14em]">
							{t("navigation.livestock")}
						</span>
						<div className="flex shrink-0 items-center gap-2 md:hidden">
							{addActions(true)}
						</div>
					</div>
					<TabNavigation
						tabs={tabs}
						activeTab={activeTab}
						size="lg"
						fullWidth
					/>
				</div>
			</div>

			{isDerivingTab ? (
				<output className="block space-y-3" aria-label={t("common.loading")}>
					<CardSkeleton className="h-24 p-4" />
					<CardSkeleton className="h-24 p-4" />
					<CardSkeleton className="h-24 p-4" />
				</output>
			) : activeTab === "herds" ? (
				<AllHerdsList
					orgId={orgId}
					scope={scope}
					// A create button in the empty state needs a concrete target farm.
					// A single farm in scope is one; a single-farm org is the other.
					inferredFarmId={
						scope.kind === "single" ? scope.establishmentId : inferredFarmId
					}
				/>
			) : (
				<>
					{/* The empty animals list is where the import matters most: the
					    producer has nothing yet, and their herd is already written down
					    somewhere. Offered here rather than inside the list's empty state
					    so the affordance survives whatever cohort filter is active — a
					    zero count is a zero count. Rendered only once the count is
					    known, so it never flashes on a loading org.

					    Manual creation leads and import follows, matching the setup
					    guide's own order (plan §7.3). */}
					{dashboard?.kpis.totalAnimals === 0 ? (
						<div className="flex flex-col items-start gap-3 rounded-2xl border border-base-300 border-dashed bg-base-100 p-4 sm:flex-row sm:items-center sm:justify-between">
							<p className="text-base-content/70 text-sm">
								{t("livestock.animalImport.emptyStateHint")}
							</p>
							<div className="flex flex-wrap items-center gap-2">
								<Button size="touch" onClick={() => handleAdd("animal")}>
									<Plus className="mr-2 h-4 w-4" />
									{t("livestock.animal.create.button")}
								</Button>
								<OfflineActionGuard blocked={blocked}>
									<Button
										variant="outline"
										size="touch"
										onClick={() => handleAdd("import")}
										aria-disabled={blocked}
									>
										<Upload className="mr-2 h-4 w-4" />
										{t("livestock.animalImport.importAction")}
									</Button>
								</OfflineActionGuard>
							</div>
						</div>
					) : null}
					<AllAnimalsList orgId={orgId} scope={scope} />
				</>
			)}

			{/* All Farms create needs a concrete target farm before the form. */}
			<ResponsiveFormDialog
				open={addTarget !== null}
				onOpenChange={(open) => !open && setAddTarget(null)}
				title={
					addTarget === "herd"
						? t("livestock.dashboard.quickActions.addHerd")
						: addTarget === "import"
							? t("livestock.animalImport.importAction")
							: t("livestock.animal.create.button")
				}
				footer={
					<>
						<Button variant="outline" onClick={() => setAddTarget(null)}>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleConfirmFarm} disabled={!pickerFarmId}>
							{t("common.continue")}
						</Button>
					</>
				}
			>
				<div className="grid gap-2">
					<Label htmlFor={farmPickerId}>{t("livestock.farm.selectFarm")}</Label>
					<Select value={pickerFarmId} onValueChange={setPickerFarmId}>
						<SelectTrigger id={farmPickerId} className="w-full">
							<SelectValue placeholder={t("common.selectEstablishment")} />
						</SelectTrigger>
						<SelectContent>
							{establishments.map((establishment) => (
								<SelectItem key={establishment.id} value={establishment.id}>
									{establishment.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</ResponsiveFormDialog>
		</div>
	);
}
